import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import type { MeResponse } from '@kominiarz/shared';
import { ApiError, setCsrfToken } from './api';
import { onReconnect } from './appStatus';

export const ME_KEY = ['me'] as const;

/** Session problems anywhere in the app bring the user back to sign-in / password change. */
function onApiError(err: unknown) {
  if (!(err instanceof ApiError)) return;
  if (err.status === 401) {
    setCsrfToken(null);
    queryClient.setQueryData<MeResponse>(ME_KEY, { user: null, csrfToken: null });
  } else if (err.code === 'PASSWORD_CHANGE_REQUIRED' || err.code === 'CSRF') {
    void queryClient.invalidateQueries({ queryKey: ME_KEY });
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onApiError }),
  mutationCache: new MutationCache({ onError: onApiError }),
  defaultOptions: {
    queries: {
      // Always run the query function: without a connection it answers from the offline copy.
      networkMode: 'always',
      staleTime: 30_000,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
    // Fail fast with a clear message instead of silently waiting for a connection.
    mutations: { retry: false, networkMode: 'always' },
  },
});

// Back online: refresh what is on screen (it may come from the offline copy).
onReconnect(() => void queryClient.invalidateQueries());
